import React, { useState } from 'react';
import {
  Table, Tag, Space, Button, Card, Form, Input, Select,
  Cascader, DatePicker, Typography, Tooltip
} from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// ══════════════════════════════════════════════
//  常量：产品类目（与产品开发页一致的三级级联）
// ══════════════════════════════════════════════

const categoryOptions = [
  {
    label: '摄像机', value: '1',
    children: [
      {
        label: '室内摄像机', value: '1-1',
        children: [
          { label: '卡片机 (Wi-Fi)', value: '111' },
          { label: '云台摄像机 (Wi-Fi)', value: '112' },
          { label: '电池摄像机 (Wi-Fi)', value: '113' },
        ]
      },
      {
        label: '户外摄像机', value: '1-2',
        children: [
          { label: '长电枪机 (Wi-Fi)', value: '121' },
          { label: '长电球机 (Wi-Fi)', value: '122' },
          { label: '电池枪机 (Wi-Fi)', value: '123' },
          { label: '电池球机 (Wi-Fi)', value: '124' },
          { label: '电池AOV摄像机 (Wi-Fi)', value: '125' },
        ]
      },
      {
        label: '4G智能摄像机', value: '1-3',
        children: [
          { label: '长电枪机 (4G)', value: '131' },
          { label: '长电球机 (4G)', value: '132' },
          { label: '电池枪机 (4G)', value: '133' },
          { label: '电池球机 (4G)', value: '134' },
        ]
      },
    ]
  },
  {
    label: '网关类', value: '2',
    children: [
      { label: 'HUB', value: '21' },
    ]
  },
  {
    label: '存储类', value: '3',
    children: [
      { label: 'NVR', value: '31' },
    ]
  }
];

const sourceOptions = [
  { value: 'self', label: '自研' },
  {
    value: 'ecosystem', label: '方案派生',
    children: [
      { value: 'P_ECO_001', label: '智能云台摄像机方案' },
      { value: 'P_ECO_002', label: '4G低功耗电池摄像机方案' },
      { value: 'P_ECO_004', label: 'NVR网络录像机方案' },
    ]
  },
  {
    value: 'transferred', label: '引用产品',
    children: [
      { value: 'ENT_8802', label: '蓝海工业自动化' },
      { value: 'HM_8827_X', label: '深圳汇云科技' },
    ]
  },
];

// ══════════════════════════════════════════════
//  Mock 数据（该企业已发布产品）
//  TODO: 联调时替换为接口数据，按 enterprise_id + status=已发布 查询
// ══════════════════════════════════════════════

const MOCK_PRODUCTS = [
  {
    key: '1', productName: 'test-4G低功耗电池摄像机', productId: 'test-prod-001',
    category: '摄像机', source: 'self', status: '已发布',
    firmwareCount: 4, price: '2.50', testQuota: 10, productionQuota: 5,
    producedNum: 156, createTime: '2026-01-15', enterpriseName: 'test-深圳智创科技有限公司', enterpriseId: 'test-ent-001',
  },
  {
    key: '2', productName: 'test-智能云台摄像机-高配版', productId: 'test-prod-002',
    category: '摄像机', source: 'ecosystem', status: '已发布',
    vendorName: '深圳芯视界科技有限公司', vendorId: 'ENT_V01', vendorProductName: '智能云台摄像机方案', vendorProductId: 'P_ECO_001',
    firmwareCount: 3, price: '1.00', testQuota: 5, productionQuota: 0,
    producedNum: 200, createTime: '2026-04-21', enterpriseName: 'test-深圳智创科技有限公司', enterpriseId: 'test-ent-001',
  },
  {
    key: '3', productName: 'test-工业网关 G100', productId: 'test-prod-003',
    category: '网关类', source: 'transferred', status: '已发布',
    vendorName: '蓝海工业自动化',
    firmwareCount: 2, price: '0.00', testQuota: 0, productionQuota: 0,
    producedNum: 0, createTime: '2026-05-05', enterpriseName: 'test-深圳智创科技有限公司', enterpriseId: 'test-ent-001',
  },
  {
    key: '4', productName: 'test-4G摄像机', productId: 'test-prod-004',
    category: '摄像机', source: 'transferred', status: '已发布',
    vendorName: '深圳汇云科技',
    firmwareCount: 1, price: '0.00', testQuota: 0, productionQuota: 0,
    producedNum: 0, createTime: '2026-05-09', enterpriseName: 'test-深圳智创科技有限公司', enterpriseId: 'test-ent-001',
  },
  {
    key: '5', productName: 'test-NVR网络录像机-标准版', productId: 'test-prod-005',
    category: '存储类', source: 'ecosystem', status: '已发布',
    vendorName: '东莞康林物联网有限公司', vendorId: 'ENT_V03', vendorProductName: 'NVR网络录像机方案', vendorProductId: 'P_ECO_004',
    firmwareCount: 5, price: '3.00', testQuota: 10, productionQuota: 8,
    producedNum: 320, createTime: '2026-03-18', enterpriseName: 'test-深圳智创科技有限公司', enterpriseId: 'test-ent-001',
  },
  {
    key: '6', productName: 'test-智能门铃', productId: 'test-prod-006',
    category: '摄像机', source: 'self', status: '已发布',
    firmwareCount: 2, price: '1.80', testQuota: 10, productionQuota: 0,
    producedNum: 45, createTime: '2026-02-10', enterpriseName: 'test-深圳智创科技有限公司', enterpriseId: 'test-ent-001',
  },
];

// ══════════════════════════════════════════════
//  主组件：已对接产品 Tab
// ══════════════════════════════════════════════

const ConnectedProductsTab = ({ enterpriseId }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({});

  const filteredProducts = MOCK_PRODUCTS.filter((p) => {
    if (enterpriseId && p.enterpriseId !== enterpriseId) return false;
    if (filters.productKey && filters.productNameValue) {
      const kw = String(filters.productNameValue).toLowerCase();
      const field = filters.productKey === 'productId' ? p.productId : p.productName;
      if (!field.toLowerCase().includes(kw)) return false;
    }
    if (filters.source) {
      const sourceVal = Array.isArray(filters.source) ? filters.source[0] : filters.source;
      if (p.source !== sourceVal) return false;
    }
    if (filters.category && Array.isArray(filters.category) && filters.category.length > 0) {
      // Cascader 值为数组，取一级类目编码匹配（mock 数据类目到一级）
      const level1Code = filters.category[0];
      const catNameMap = { '1': '摄像机', '2': '网关类', '3': '存储类' };
      if (p.category !== catNameMap[level1Code]) return false;
    }
    if (filters.createTime) {
      const [start, end] = filters.createTime;
      if (start && end) {
        const t = p.createTime;
        if (t < start.format('YYYY-MM-DD') || t > end.format('YYYY-MM-DD')) return false;
      }
    }
    return true;
  });

  const handleSearch = () => {
    setFilters(form.getFieldsValue());
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({});
  };

  const columns = [
    {
      title: '产品信息', key: 'productInfo', width: 200,
      render: (_, record) => (
        <div>
          <div
            style={{ color: '#1890ff', cursor: 'pointer' }}
            onClick={() => navigate('/product/info', { state: record })}
          >
            {record.productName}
          </div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.productId}</div>
        </div>
      )
    },
    { title: '产品类目', dataIndex: 'category', key: 'category', width: 140 },
    {
      title: '开发方式', key: 'source', width: 140,
      render: (_, record) => {
        if (record.source === 'ecosystem') {
          return (
            <Tooltip title={`方案产品：${record.vendorProductName || '-'}\n方案商：${record.vendorName || '-'}`}>
              <Tag color="blue">方案派生</Tag>
            </Tooltip>
          );
        }
        if (record.source === 'transferred') {
          return (
            <Tooltip title={`来源企业：${record.vendorName || '-'}`}>
              <Tag color="orange">引用产品</Tag>
            </Tooltip>
          );
        }
        return <Tag color="green">自研</Tag>;
      }
    },
    {
      title: '产品状态', dataIndex: 'status', key: 'status', width: 100,
      render: (status) => <Tag color="green">{status}</Tag>
    },
    { title: '固件数量', dataIndex: 'firmwareCount', key: 'firmwareCount', width: 90 },
    { title: '授权单价 (元/个)', dataIndex: 'price', key: 'price', width: 130 },
    {
      title: '赠送授权', key: 'freeQuota', width: 140,
      render: (_, record) => (
        <div style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
          <div>测试授权：{record.testQuota || 0}个</div>
          <div>生产授权：{record.productionQuota || 0}个</div>
        </div>
      )
    },
    { title: '采购生产授权', dataIndex: 'producedNum', key: 'producedNum', width: 120 },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 110 },
    {
      title: '操作', key: 'action', fixed: 'right', width: 100,
      render: (_, record) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', fontSize: '12px', color: '#1890ff' }}
          onClick={() => navigate('/product/audit/manage', { state: record })}
        >
          管理产品
        </Button>
      )
    },
  ];

  return (
    <div>
      {/* 查询区 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0' }}>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="productKey" noStyle initialValue="productName">
                <Select style={{ width: 100 }} onChange={() => form.setFieldsValue({ productNameValue: undefined })}>
                  <Option value="productName">产品名称</Option>
                  <Option value="productId">产品ID</Option>
                </Select>
              </Form.Item>
              <Form.Item name="productNameValue" noStyle>
                <Input style={{ width: 180 }} placeholder="请输入，输入test可查询" allowClear />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="source" style={{ marginRight: 8 }}>
            <Cascader options={sourceOptions} placeholder="开发方式" changeOnSelect allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="category" style={{ marginRight: 8 }}>
            <Cascader options={categoryOptions} placeholder="产品类目" changeOnSelect allowClear style={{ width: 140 }} />
          </Form.Item>
          <Form.Item name="createTime" style={{ marginRight: 8 }}>
            <RangePicker placeholder={['开始时间', '结束时间']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 列表区 */}
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <div style={{ marginBottom: 16 }}>
          <Text strong>产品总数：</Text>
          <Text style={{ fontSize: 18, fontWeight: 600, color: '#1677ff' }}>{filteredProducts.length}</Text>
        </div>
        <Table
          columns={columns}
          dataSource={filteredProducts}
          rowKey="key"
          scroll={{ x: 1300 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          locale={{
            emptyText: (
              <div style={{ padding: 40 }}>
                <Text type="secondary">该企业暂无已发布产品，请调整查询条件</Text>
              </div>
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default ConnectedProductsTab;
